import Link from 'next/link';

import { PlayerAvatar } from '@components/predicted-lineups/PlayerAvatar';
import { toPlayerByIdPath } from '@lib/page-utils/player-profile/slugs';
import type { PlayerFactsMap } from '@lib/predicted-lineups/player-facts';
import type { SanityLineupAvailabilityEntry } from '@lib/sanity';

type Tone = 'danger' | 'warning' | 'neutral';

const TAG_TONE: Record<Tone, string> = {
  danger: 'bg-[#ffe0e6] text-[#8f1d38]',
  warning: 'bg-[#fff1bf] text-[#6b4f00]',
  neutral: 'bg-[#e9ecef] text-[#3c4249]'
};

type AvailabilityListProps = {
  title: string;
  entries?: SanityLineupAvailabilityEntry[];
  playerFacts: PlayerFactsMap;
  tone: Tone;
  testId: string;
};

/**
 * One availability group as a row of pills: the status tag first, then a pill
 * per player with their face, name and the editor's one-word reason. Rows
 * wrap, so a long injury list costs two lines rather than a column of ten.
 */
export const AvailabilityList = ({
  title,
  entries,
  playerFacts,
  tone,
  testId
}: AvailabilityListProps) => {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-cy={testId}>
      <span
        className={`rounded-full px-2.5 py-1 font-body text-[12px] font-semibold leading-none ${TAG_TONE[tone]}`}
      >
        {title}
      </span>
      {entries.map((entry) => {
        const facts = playerFacts[entry.player.optaUuid];
        const name = entry.player.displayName ?? facts?.displayName ?? 'Player';
        return (
          <Link
            key={entry._key}
            href={toPlayerByIdPath(entry.player.optaUuid)}
            className="inline-flex items-center gap-2 rounded-full border border-[#e4e7ea] bg-white py-1 pl-1 pr-3 no-underline hover:border-[#a7adb3]"
          >
            <PlayerAvatar
              imageUrl={facts?.imageUrl}
              className="h-6 w-6 shrink-0"
            />
            <span className="font-body text-[13px] font-semibold leading-none !text-[#14171c]">
              {name}
            </span>
            {entry.detail && (
              <span className="font-body text-[12px] leading-none text-[#767d85]">
                {entry.detail}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
};
